/**
 * Headless selectors + formatters for the Insights → Monthly tab.
 *
 * Platform-neutral (no DOM, no recharts) per the shared-core contract.
 * The web-shell chart component consumes these to project the two revenue
 * lenses plus the quoting overlay (quoted value / gross quoted) and to render
 * KPI/tooltip figures consistently.
 *
 * Vocabulary (locked): "quoted value" = estimate value deduped to one row per
 * enquiry (all outcomes — the real quoting demand); "gross quoted" = raw
 * sum(estimate_amt) over every revision (inflated ~2×); "won value" = quoted
 * value filtered to won.
 */

import type { InsightsMonthlyRevenuePoint } from "../data/api-types";
import { SHORT_MONTHS } from "./formatters";

export type RevenueBasis = "booked" | "invoiced";

/** One month projected to the selected basis — the shape the chart plots. */
export interface MonthlyRevenueDatum {
  period: string;
  /** Short axis label, e.g. "Jun 25". */
  label: string;
  revenue: number;
  contribution: number;
  /** contribution / revenue as a percentage (0 when revenue is 0). */
  marginPct: number;
  /** Deduped one-row-per-enquiry estimate value (the amber solid line). */
  quotedValue: number;
  /** Raw pre-dedupe sum over every revision (the dashed line). */
  grossQuoted: number;
  /** Quoted value filtered to won. */
  wonValue: number;
  /**
   * True for the final month, which is month-to-date (partial): faded in the
   * chart and excluded from the quoting lines and the period totals.
   */
  isPartial: boolean;
}

/** Month-start ISO ("YYYY-MM") → "Jun 25" style short label. */
export function shortMonthLabel(period: string): string {
  const [year, month] = period.split("-").map((n) => parseInt(n, 10));
  if (!year || !month) return period;
  // Fixed month table, not Intl: avoids ICU rendering en-GB's short
  // September as "Sept" while every other label in the app says "Sep".
  return `${SHORT_MONTHS[month - 1]} ${String(year).slice(-2)}`;
}

/** Project each month to the chosen revenue basis (booked or invoiced). */
export function projectMonthlyRevenue(
  months: InsightsMonthlyRevenuePoint[],
  basis: RevenueBasis,
): MonthlyRevenueDatum[] {
  const lastIndex = months.length - 1;
  return months.map((m, i) => {
    const revenue = basis === "booked" ? m.booked_revenue : m.invoiced_revenue;
    const contribution =
      basis === "booked" ? m.booked_contribution : m.invoiced_contribution;
    return {
      period: m.period,
      label: shortMonthLabel(m.period),
      revenue,
      contribution,
      marginPct: revenue ? (100 * contribution) / revenue : 0,
      quotedValue: m.quoted_value ?? 0,
      grossQuoted: m.gross_quoted ?? 0,
      wonValue: m.won_value ?? 0,
      isPartial: i === lastIndex,
    };
  });
}

/** The full (non-partial) months — the basis for lines and period totals. */
export function fullMonths(data: MonthlyRevenueDatum[]): MonthlyRevenueDatum[] {
  return data.filter((d) => !d.isPartial);
}

/** Latest full-month revenue for the KPI strip (0 when the series is empty). */
export function latestMonthRevenue(data: MonthlyRevenueDatum[]): number {
  const full = fullMonths(data);
  return full.length ? full[full.length - 1].revenue : 0;
}

/** Latest full-month contribution margin %, for the KPI strip. */
export function latestMonthMarginPct(data: MonthlyRevenueDatum[]): number {
  const full = fullMonths(data);
  return full.length ? full[full.length - 1].marginPct : 0;
}

/** First full-month contribution margin % — the "was X%" delta anchor. */
export function firstMonthMarginPct(data: MonthlyRevenueDatum[]): number {
  const full = fullMonths(data);
  return full.length ? full[0].marginPct : 0;
}

/** First full-month short label (e.g. "Aug 24") — for the "was X% (Aug 24)" note. */
export function firstMonthLabel(data: MonthlyRevenueDatum[]): string {
  const full = fullMonths(data);
  return full.length ? full[0].label : "";
}

/** Sum of quoted value over the full months (period total). */
export function periodQuotedValue(data: MonthlyRevenueDatum[]): number {
  return fullMonths(data).reduce((s, d) => s + d.quotedValue, 0);
}

/** Sum of gross quoted over the full months (period total). */
export function periodGrossQuoted(data: MonthlyRevenueDatum[]): number {
  return fullMonths(data).reduce((s, d) => s + d.grossQuoted, 0);
}

/** Sum of revenue over the full months (for the ×revenue multiples). */
export function periodRevenue(data: MonthlyRevenueDatum[]): number {
  return fullMonths(data).reduce((s, d) => s + d.revenue, 0);
}

/** Quoted-value ÷ revenue multiple over the period (0 when no revenue). */
export function quotedRevenueMultiple(data: MonthlyRevenueDatum[]): number {
  const rev = periodRevenue(data);
  return rev ? periodQuotedValue(data) / rev : 0;
}

/**
 * Dedup correction: the % of gross quoted removed by deduping to quoted value,
 * i.e. (gross − quoted) / gross. 0 when there is no gross. Returned as a
 * positive fraction (the KPI renders it as "−N%").
 */
export function dedupCorrectionPct(data: MonthlyRevenueDatum[]): number {
  const gross = periodGrossQuoted(data);
  if (!gross) return 0;
  return (100 * (gross - periodQuotedValue(data))) / gross;
}

/** Gross ÷ quoted inflation multiple (how much revisions inflate gross). */
export function grossInflationMultiple(data: MonthlyRevenueDatum[]): number {
  const quoted = periodQuotedValue(data);
  return quoted ? periodGrossQuoted(data) / quoted : 0;
}

/**
 * Smallest "nice" axis maximum ≥ `value`, chosen so a `[0, max]` axis divides
 * into `tickCount − 1` equal, round steps.
 *
 * Both Insights revenue charts are dual-axis (revenue/contribution bars left,
 * quoting lines right) and every plotted value is ≥ 0, so neither axis should
 * ever show a negative tick. recharts only honours a *numeric* domain floor
 * reliably on a dual-axis chart: with `domain={[0, "auto"]}` its nice-tick pass
 * is free to re-centre the domain so the two axes' tick rows align, and that
 * can push the left axis below zero — reading as fake negative revenue. Passing
 * an explicit `[0, niceAxisMax(dataMax)]` to each axis removes that freedom
 * while keeping equal row counts (and therefore aligned gridlines) across both.
 */
export function niceAxisMax(value: number, tickCount = 5): number {
  const intervals = Math.max(1, tickCount - 1);
  if (!Number.isFinite(value) || value <= 0) return intervals;
  const rawStep = value / intervals;
  const magnitude = Math.pow(10, Math.floor(Math.log10(rawStep)));
  const normalized = rawStep / magnitude;
  const niceUnit =
    [1, 2, 2.5, 3, 4, 5, 6, 8, 10].find((u) => normalized <= u) ?? 10;
  return niceUnit * magnitude * intervals;
}

/** Compact £ formatter — "£1.10m" / "£835k" / "£0". */
export function formatGbpCompact(n: number): string {
  if (!Number.isFinite(n) || n === 0) return "£0";
  const sign = n < 0 ? "-" : "";
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return `${sign}£${(abs / 1_000_000).toFixed(2)}m`;
  if (abs >= 1_000) return `${sign}£${Math.round(abs / 1_000)}k`;
  return `${sign}£${Math.round(abs)}`;
}
